import { Component, ElementRef, HostListener, OnInit, inject, viewChildren } from '@angular/core';
import { RouterLink } from '@angular/router';
import { InternationalizationService, PlaySoundsService, SessionService } from '@app/core/services';

export interface Slide {
  title: string;
  images: string[];
  content: string[];
}

@Component({
    selector: 'app-rules',
    templateUrl: './rules.component.html',
    styleUrls: ['./rules.component.scss'],
    imports: [RouterLink]
})
export class RulesComponent implements OnInit {
  public slidesList = viewChildren<ElementRef<HTMLElement>>('slide');

  slides: Slide[];
  activeSlideIndex: number;
  dragActive: boolean;
  startX: number;
  endX: number;

  public i18n = inject(InternationalizationService);
  public soundsService = inject(PlaySoundsService);
  private sessionService = inject(SessionService);

  ngOnInit(): void {
    this.activeSlideIndex = 0;

    this.dragActive = false;
    this.startX = 0;
    this.endX = 0;

    this.slides = [
      {
        title: this.i18n.str('challengeu.rules_general_information_title', "General information")(),
        images: [
          'assets/app-branding/img/jetons/red.png',
          'assets/app-branding/img/jetons/green.png',
          'assets/app-branding/img/jetons/black.png',
          'assets/app-branding/img/jetons/blue.png',
          'assets/app-branding/img/jetons/yellow.png',
        ],
        content: [
          this.i18n.str('challengeu.rules_general_information_content_0', "The game has 12 topics with several questions each. The more points a question brings, the more difficult it is.")()
        ]
      },
      {
        title: this.i18n.str('challengeu.rules_select_question_title', "Selecting a question")(),
        images: [],
        content: [
          this.i18n.str('challengeu.rules_select_question_content_0', `Before every question, you need to choose a topic and a question (level of difficulty). When you choose a question, it is displayed on the screen together with the possible answers.
          Once a question is selected, it is not available any more (it can't be selected again).`)()
        ]
      },
      {
        title: this.i18n.str('challengeu.rules_scoring_title', "Scoring")(),
        images: [
          'assets/app-branding/img/countdown-rules.png'
        ],
        content: [
          this.i18n.str('challengeu.rules_scoring_content_0', `If you answer correctly, you win the number of points for the question. For a wrong answer, you would lose half of the points.
          If you do not answer to your question or the times for the question is over (countdown reaches 0), it is considered as a wrong answer and you lose half of the points for the question.`)(),
          this.i18n.str('challengeu.rules_scoring_content_1', `You have 20 seconds to answers questions for 100 and 200 points, 30 seconds for 300-400 points, 40 seconds for 500 points.`)(),
        ]
      },
      {
        title: this.i18n.str('challengeu.rules_jokers_title', "Jokers")(),
        images: [
          'assets/app-branding/img/question/Icon-joker2.png',
          'assets/app-branding/img/question/Icon-skipquestion.png',
        ],
        content: [
          this.i18n.str('challengeu.rules_jokers_content_0', `When you choose a question, the systems shows you the eligible/remaining jokers. The jokers are:`)(),
          this.i18n.str('challengeu.rules_jokers_content_1', `50/50 (can be used only once) - the computer eliminates randomly 2 wrong answers. Background players cannot use it.`)(),
          this.i18n.str('challengeu.rules_jokers_content_2', `Skip a question (can be used max 3 times during the game). The use of this joker does not affect your score - neither gain, nor lose points. Background players cannot use it.`)(),
        ]
      },
          ];
  }

  public changeSlide(event: MouseEvent, direction: string) {
    if(event) {
      event.preventDefault();
    }

    if (!this.sessionService.getSession()?.soundsOff) {
      this.soundsService.playSound('rule-slide');
    }

    if(this.activeSlideIndex === 0 && direction === 'left') {
      return;
    }
    if(this.activeSlideIndex === this.slides.length-1 && direction === 'right') {
      return;
    }

    if(direction === 'left') {
      this.activeSlideIndex--;
    } else {
      this.activeSlideIndex++;
    }

    this.selectSlide(null, this.activeSlideIndex);
  }

  public selectSlide(event: Event | null, index: number) {
    if(event) {
      event.preventDefault();
    }

    this.setActiveSlide(index);
    this.updateSlidePosition();
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.updateSlidePosition();
  }

  private updateSlidePosition() {
    const slides = this.slidesList();
    const firstSlide = slides[0]?.nativeElement;
    const activeSlide = slides[this.activeSlideIndex]?.nativeElement;

    if (!firstSlide || !activeSlide) {
      return;
    }

    const translateX = activeSlide.offsetLeft - firstSlide.offsetLeft;

    slides.forEach(el => {
      el.nativeElement.style.transform = `translateX(-${translateX}px)`;
    });
  }

  public setActiveSlide(index: number) {
    this.activeSlideIndex = index;
  }

  public startDrag(ev: MouseEvent | TouchEvent) {
    ev.preventDefault();
    this.dragActive = true;

    this.startX = ev.type === 'touchstart'
      ? (ev as TouchEvent)?.targetTouches[0]?.clientX
      : (ev as MouseEvent)?.clientX;
  }

  public endDrag(ev: MouseEvent | TouchEvent) {
    ev.preventDefault();

    if(ev.type !== 'touchend') {
      this.endX = (ev as MouseEvent)?.clientX;
    }

    this.dragActive = false;
    const diff = this.endX - this.startX;

    if(Math.abs(diff) > 0) {
      if(diff > 0) {
        this.changeSlide(null, 'left');
      } else {
        this.changeSlide(null, 'right');
      }
    }
  }

  public onPointerMove(ev: MouseEvent | TouchEvent) {
    if(this.dragActive) {
      if(ev.type === 'touchmove') {
        this.endX = (ev as TouchEvent)?.targetTouches[0]?.clientX;
      }
    }
  }
}
